import React from 'react';
import styled, { keyframes, css } from 'styled-components';
import { FieldGroup } from '../../styles/index';

const slideUp = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0.6;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const slideDown = keyframes`
  from {
    transform: translateY(0);
    opacity: 1;
  }
  to {
    transform: translateY(100%);
    opacity: 0.6;
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to   { opacity: 0; }
`;


export const MobileOverlay = styled.div<{ isAnimatingOut: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  z-index: 1200;
  animation: ${({ isAnimatingOut }) =>
    isAnimatingOut
      ? css`${fadeOut} 0.2s ease-in forwards`
      : css`${fadeIn} 0.3s ease-out forwards`};
`;


export const FullScreenContainer = styled.div<{ isAnimatingOut: boolean }>`
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100dvh; 
  margin: 0;
  padding: 0;
  border: none;
  border-radius: 0;
  background: ${({ theme }) => theme.colors.modalBackground || '#fff'};
  color: ${({ theme }) => theme.colors.text};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 1201;
  animation: ${({ isAnimatingOut }) =>
    isAnimatingOut
      ? css`${slideDown} 0.2s ease-in forwards`
      : css`${slideUp} 0.3s ease-out forwards`};
  will-change: transform, opacity;

  @supports not (height: 100dvh) {
    height: 100vh;
    height: -webkit-fill-available;
  }
`;


export const FixedHeader = styled.header`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 16px;
  padding-top: env(safe-area-inset-top, 0px); 
  min-height: 56px;
  background: ${({ theme }) => theme.colors.modalBackground || '#fff'};
  border-bottom: 1px solid ${({ theme }) => theme.colors.inputBorder};
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  z-index: 10;
  position: sticky;
  top: 0;

  @media (max-height: 500px) and (orientation: landscape) {
    min-height: 44px;
    padding: 0 12px;
  }
`;

export const HeaderCloseButton = styled.button`
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  min-width: 44px; 
  border-radius: 50%;
  border: 1px solid transparent;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text};
  transition: background 0.12s ease, transform 0.08s ease;

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }

  @media (max-height: 500px) and (orientation: landscape) {
    width: 36px;
    height: 36px;
    min-width: 36px;
    font-size: 16px;
  }
`;

export const HeaderTitle = styled.h2`
  flex: 1;
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
  gap: 8px;

  &::before {
    content: '';
    flex-shrink: 0;
    width: 8px;
    height: 8px;
    background: ${({ theme }) => theme.colors.primary};
    border-radius: 50%;
    display: inline-block;
  }

  @media (max-height: 500px) and (orientation: landscape) {
    font-size: 0.875rem;
  }
`;

export const SwipeHandle = styled.div`
  position: absolute;
  top: calc(env(safe-area-inset-top, 0px) + 6px);
  left: 50%;
  transform: translateX(-50%);
  width: 36px;
  height: 4px;
  border-radius: 2px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)'};
  pointer-events: none;
`;


export const ScrollableContent = styled.main`
  flex: 1 1 0;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  padding: 16px 16px 8px;
  display: flex;
  flex-direction: column;
  gap: 0;

  min-height: 0;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.scrollThumb || '#ccc'};
    border-radius: 2px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }

  @media (max-height: 500px) and (orientation: landscape) {
    padding: 10px 12px 6px;
  }

  @media (max-width: 360px) {
    padding: 12px 12px 6px;
  }
`;

export const ContentSection = styled.section`
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const ContentSectionTitle = styled.h3`
  font-size: 0.8rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  opacity: 0.6;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin: 0 0 10px 0;
  padding-bottom: 6px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.inputBorder};
`;

export const SubSectionTitle = styled.h4`
  font-size: 0.78rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 12px 0 6px;
`;


export const FixedFooter = styled.footer`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 6px); /* notch */
  background: ${({ theme }) => theme.colors.modalBackground || '#fff'};
  border-top: 1px solid ${({ theme }) => theme.colors.modalFooterBorder || theme.colors.inputBorder};
  box-shadow: 0 -1px 4px rgba(0, 0, 0, 0.06);
  position: sticky;
  bottom: 0;
  z-index: 10;

  @media (max-height: 500px) and (orientation: landscape) {
    padding: 4px 10px;
    padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 4px);
    gap: 4px;
  }

  @media (max-width: 360px) {
    gap: 4px;
    padding: 5px 10px;
    padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 5px);
  }
`;

const footerButtonBase = css`
  flex: 1;
  min-height: 32px;
  padding: 0 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border-radius: 8px;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.08s ease, box-shadow 0.12s ease, opacity 0.12s ease;
  white-space: nowrap;
  border: none;
  outline: none;

  &:active {
    transform: scale(0.97);
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }: any) => theme?.colors?.primary || '#00348A'};
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }


  @media (max-height: 500px) and (orientation: landscape) {
    min-height: 28px;
    font-size: 0.72rem;
    border-radius: 6px;
  }

  @media (max-width: 360px) {
    font-size: 0.7rem;
    padding: 0 6px;
    border-radius: 6px;
  }
`;

export const FooterCancelButton = styled.button`
  ${footerButtonBase}
  background: ${({ theme }) => theme.colors.buttonCancelDarkBackground};
  color: ${({ theme }) => theme.colors.buttonCancelDarkText};
  border: 1px solid ${({ theme }) => theme.colors.inputBorder};
  flex: 1.2;
`;

export const FooterFavoriteButton = styled.button<{ isFavorite: boolean }>`
  ${footerButtonBase}
  flex: 0 0 32px;
  min-width: 32px;
  padding: 0;
  border-radius: 50%;
  background: ${({ theme, isFavorite }) =>
    isFavorite
      ? `${theme.colors.primary}18`
      : theme.mode === 'dark'
      ? 'rgba(255,255,255,0.06)'
      : 'rgba(0,0,0,0.04)'};
  color: ${({ theme, isFavorite }) =>
    isFavorite ? theme.colors.primary : theme.colors.text};
  border: 1px solid
    ${({ theme, isFavorite }) =>
      isFavorite ? theme.colors.primary : theme.colors.inputBorder};
  font-size: 0.95rem;
  transition: background 0.15s ease, transform 0.08s ease, color 0.15s ease;

  &:active {
    transform: scale(0.88);
  }

  @media (max-height: 500px) and (orientation: landscape) {
    flex: 0 0 26px;
    min-width: 26px;
    font-size: 0.8rem;
  }
`;

export const FooterSaveButton = styled.button`
  ${footerButtonBase}
  background: ${({ theme }) => theme.colors.primary || '#00348A'};
  color: #fff;
  flex: 1.5;
  box-shadow: 0 2px 8px ${({ theme }) => `${theme.colors.primary}40` || 'rgba(0,52,138,0.25)'};

  &:hover:not(:disabled) {
    box-shadow: 0 4px 14px
      ${({ theme }) => `${theme.colors.primary}55` || 'rgba(0,52,138,0.35)'};
  }
`;


export const MobileFieldGroup = styled.div`
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const MobileFieldLabel = styled.label`
  display: block;
  font-size: 0.8rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.fieldLabel};
  margin-bottom: 4px;
`;


export const InfoRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 0.82rem;
  color: ${({ theme }) => theme.colors.text};
`;

export const InfoLabel = styled.span`
  opacity: 0.55;
  font-size: 0.75rem;
`;

export const InfoValue = styled.strong`
  color: ${({ theme }) => theme.colors.text};
`;


export const SavingBar = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: ${({ theme }) => theme.colors.primary};
  border-radius: 0;
  animation: ${keyframes`
    0%   { width: 0%; left: 0; }
    50%  { width: 70%; left: 0; }
    100% { width: 0%;  left: 100%; }
  `} 1.4s ease-in-out infinite;
  z-index: 20;
`;


export const errorInputStyle: React.CSSProperties = {
  borderColor: '#f44336',
  boxShadow: '0 0 0 2px rgba(244,67,54,0.15)',
};


export const RequiredAsterisk = styled.span`
  color: #f44336;
  margin-left: 3px;
`;

export const ClickableReadonlyField = styled.div<{ $hasError?: boolean; $empty?: boolean }>`
  padding: 8px 12px;
  border-radius: 10px;
  border: 1px solid ${({ $hasError }) => ($hasError ? '#f44336' : 'rgba(0,0,0,0.1)')};
  min-height: 38px;
  cursor: pointer;
  font-size: 0.85rem;
  color: ${({ $empty }) => ($empty ? '#aaa' : 'inherit')};
`;

export const FieldErrorMessage = styled.div`
  color: #f44336;
  font-size: 0.75rem;
  margin-top: 4px;
`;

export const DescriptionFieldGroup = styled(FieldGroup)`
  margin-bottom: 0;
`;
